import React, { useMemo } from 'react';
import { Card, DatePicker, Select, Typography } from 'antd';
import dayjs, { type Dayjs } from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { FilterBar } from '../../../components/list';
import { useCustomerSegmentOptions } from '../../../hooks/options/useCustomerSegmentOptions';
import { useContactCategories } from '../../contacts/hooks/useContactCategories';
import { useUserOptions } from '../../../hooks/options/useUserOptions';

dayjs.extend(utc);

const { RangePicker } = DatePicker;
const { Text } = Typography;

function defaultMonthRange(): [Dayjs, Dayjs] {
  const start = dayjs().utc().startOf('month');
  return [start, start.add(1, 'month')];
}

export interface BusinessOverviewFilterState {
  dateRange: [Dayjs, Dayjs];
  segmentIds: string[];
  contactCategoryIds: number[];
  assigneeIds: string[];
}

interface Props {
  value: BusinessOverviewFilterState;
  onChange: (value: BusinessOverviewFilterState) => void;
  hasAssigneeFilter?: boolean;
}

const BusinessOverviewFilterBar: React.FC<Props> = ({ value, onChange, hasAssigneeFilter = false }) => {
  const { data: segments = [] } = useCustomerSegmentOptions();
  const { data: categoriesData } = useContactCategories();
  const { data: users = [] } = useUserOptions();

  const segmentOptions = useMemo(
    () => segments.map((s) => ({ value: s.id, label: s.label })),
    [segments]
  );

  const categoryOptions = useMemo(
    () =>
      (categoriesData ?? [])
        .filter((c) => c.isActive)
        .map((c) => ({ value: c.id, label: c.label })),
    [categoriesData]
  );

  const userOptions = useMemo(
    () => users.map((u) => ({ value: u.id, label: u.label })),
    [users]
  );

  const hasFilters =
    value.segmentIds.length > 0 ||
    value.contactCategoryIds.length > 0 ||
    value.assigneeIds.length > 0 ||
    !value.dateRange[0].isSame(defaultMonthRange()[0], 'day') ||
    !value.dateRange[1].isSame(defaultMonthRange()[1], 'day');

  const rangeLabel = `${value.dateRange[0].format('MMM D, YYYY')} – ${value.dateRange[1].subtract(1, 'day').format('MMM D, YYYY')} (UTC)`;

  return (
    <Card
      title="Filters"
      size="small"
      extra={<Text type="secondary" style={{ fontSize: 12, fontWeight: 400 }}>{rangeLabel}</Text>}
    >
      <FilterBar
        onReset={
          hasFilters
            ? () => onChange({ dateRange: defaultMonthRange(), segmentIds: [], contactCategoryIds: [], assigneeIds: [] })
            : undefined
        }
      >
        <>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <span style={{ fontSize: 12, color: 'var(--ant-color-text-tertiary)', lineHeight: '16px' }}>Date Range</span>
            <RangePicker
              value={value.dateRange}
              onChange={(dates) => {
                if (dates?.[0] && dates[1]) {
                  onChange({ ...value, dateRange: [dates[0], dates[1]] });
                }
              }}
            />
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 2, minWidth: 160, flex: '1 1 140px' }}>
            <span style={{ fontSize: 12, color: 'var(--ant-color-text-tertiary)', lineHeight: '16px' }}>Customer Segments</span>
            <Select<string[]>
              mode="multiple"
              allowClear
              options={segmentOptions}
              value={value.segmentIds}
              onChange={(segmentIds) => onChange({ ...value, segmentIds })}
              optionFilterProp="label"
              maxTagCount="responsive"
            />
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 2, minWidth: 160, flex: '1 1 140px' }}>
            <span style={{ fontSize: 12, color: 'var(--ant-color-text-tertiary)', lineHeight: '16px' }}>Contact Categories</span>
            <Select<number[]>
              mode="multiple"
              allowClear
              options={categoryOptions}
              value={value.contactCategoryIds}
              onChange={(contactCategoryIds) => onChange({ ...value, contactCategoryIds })}
              optionFilterProp="label"
              maxTagCount="responsive"
            />
          </div>
          {hasAssigneeFilter ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 2, minWidth: 160, flex: '1 1 140px' }}>
              <span style={{ fontSize: 12, color: 'var(--ant-color-text-tertiary)', lineHeight: '16px' }}>Assignees</span>
              <Select<string[]>
                mode="multiple"
                allowClear
                options={userOptions}
                value={value.assigneeIds}
                onChange={(assigneeIds) => onChange({ ...value, assigneeIds })}
                optionFilterProp="label"
                maxTagCount="responsive"
              />
            </div>
          ) : null}
        </>
      </FilterBar>
    </Card>
  );
};

export { defaultMonthRange };
export default BusinessOverviewFilterBar;
